import { supabase } from '@/lib/supabase';
import { AuthError, User } from '@supabase/supabase-js';
import { PostgrestError } from '@supabase/postgrest-js';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { appStorage } from '@/lib/appStorage';

interface UserData {
  id: string;
  nome: string;
  email: string;
  foto_perfil?: string;
  tipo_de_conta: 'free' | 'premium';
  created_at?: string;
  updated_at?: string;
  data_fim_periodo?: string;
}

interface AuthResponse {
  user: User | null;
  session?: Session | null;
  error: any;
}

// Interface para o retorno de listUsers
interface AdminUserList {
  users: User[];
  total: number;
}

// Função auxiliar para obter URL base do app
function getBaseUrl(): string {
  if (typeof window === 'undefined') {
    return 'https://cantando-escrituras.vercel.app';
  }

  return window.location.origin;
}

// Função auxiliar para tratar erros
function handleError(error: unknown): AuthError {
  const message = error instanceof Error ? error.message : 'Erro desconhecido';
  const friendlyMessages: Record<string, string> = {
    'Email já está em uso': 'Este email já está cadastrado',
    'Invalid login credentials': 'Email ou senha incorretos',
    'Email not confirmed': 'Por favor, confirme seu email antes de fazer login',
    'Invalid email': 'Email inválido',
    'Password is too short': 'A senha deve ter pelo menos 6 caracteres',
    'Timeout ao fazer login': 'O login está demorando muito. Tente novamente.',
    'Network error': 'Erro de conexão. Verifique sua internet.',
  };
  return new AuthError(friendlyMessages[message] || message);
}

export const signUp = async (email: string, password: string, nome: string): Promise<{ user: UserData | null; error: AuthError | null }> => {
  try {
    // Verifica se já existe usuário com este email
    const { data: existingProfile } = await supabase
      .from('users')
      .select('email')
      .eq('email', email)
      .single();

    if (existingProfile) {
      return {
        user: null,
        error: new AuthError('Este email já está cadastrado. Por favor, use outro email ou faça login.')
      };
    }

    // Cria usuário no auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          nome: nome
        }
      }
    });

    if (authError) {
      return { user: null, error: authError };
    }

    if (!authData?.user) {
      return {
        user: null,
        error: new AuthError('Erro ao criar usuário')
      };
    }

    // Cria/atualiza perfil do usuário
    const { data: userData, error: profileError } = await supabase
      .from('users')
      .upsert({
        id: authData.user.id,
        email: email,
        nome: nome,
        tipo_de_conta: 'free'
      })
      .select()
      .single();

    if (profileError) {
      return { user: null, error: handleError(profileError) };
    }

    return { user: userData, error: null };
  } catch (error) {
    return { user: null, error: handleError(error) };
  }
};

// Nova função para criar o perfil do usuário após confirmação do email
export async function createUserProfile(userId: string, email: string, nome: string): Promise<AuthResponse> {
  try {
    // Verifica se já existe um perfil
    const { data: existingProfile } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (existingProfile) {
      return { user: existingProfile, error: null };
    }

    // Cria o perfil do usuário
    const { data: userData, error: insertError } = await supabase
      .from('users')
      .insert([{
        id: userId,
        email,
        nome,
        tipo_de_conta: 'free'
      }])
      .select()
      .single();

    if (insertError) {
      // Se der erro ao criar perfil, deleta o usuário para manter consistência
      await supabase.functions.invoke('user-management', {
        body: { action: 'delete_user', user_id: userId }
      });
      return { user: null, error: handleError('Erro ao criar perfil. Por favor, tente novamente.') };
    }

    return { user: userData, error: null };

  } catch (error) {
    return { user: null, error: handleError(error) };
  }
}

export async function signIn(credentials: any): Promise<AuthResponse> {
  try {
    const { email, password } = credentials;

    if (!email || !password) {
      return { user: null, error: handleError('Email e senha são obrigatórios') };
    }

    // Iniciar sessão
    const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    
    if (signInError) {
      throw new Error(`Erro no login: ${signInError.message}`);
    }
    
    if (!authData.user) {
      throw new Error('Usuário não encontrado após o login.');
    }

    // Verificar se o perfil existe
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('id', authData.user.id)
      .single();

    if (userError) {
      throw new Error(`Erro ao verificar usuário: ${userError.message}`);
    }

    // Se o perfil não existir, criar
    if (!userData) {
      const { data: newUserData, error: createError } = await supabase
        .from('users')
        .insert([{
          id: authData.user.id,
          email: authData.user.email,
          nome: authData.user.user_metadata.nome || authData.user.email?.split('@')[0] || 'Usuário',
          tipo_de_conta: 'free'
        }])
        .select()
        .single();

      if (createError) {
        throw new Error(`Erro ao criar perfil: ${createError.message}`);
      }
      return { user: newUserData, error: null };
    }

    return { user: userData, error: null };
  } catch (error) {
    return { user: null, error };
  }
}

export async function signOut(): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.signOut();
    return { error: error ? handleError(error) : null };
  } catch (error) {
    return { error: handleError(error) };
  }
}

export async function getCurrentUser(): Promise<AuthResponse> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      return { user: null, error: null };
    }

    const { data: userData } = await supabase
      .from('users')
      .select('*')
      .eq('id', session.user.id)
      .single();

    return { 
      user: userData || null,
      error: null 
    };
  } catch (error) {
    return { user: null, error: handleError(error) };
  }
}

export async function deleteAccount(userId: string): Promise<{ success: boolean; error: any }> {
  try {
    if (!userId) {
      throw new Error('ID do usuário não fornecido');
    }
    
    // Chama a Edge Function para deletar o usuário e dados associados
    const { error: deleteError } = await supabase.functions.invoke('user-management', {
      body: { action: 'delete_user', user_id: userId }
    });

    if (deleteError) {
      throw new Error(`Erro ao deletar conta: ${deleteError.message}`);
    }

    // Deslogar localmente
    await supabase.auth.signOut();

    return { success: true, error: null };
  } catch (error) {
    return { success: false, error };
  }
}

// Função para login com Google
export async function signInWithGoogle(): Promise<void> {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: 'com.biblia.em.louvor.app://auth/callback',
        skipBrowserRedirect: true
      }
    });

    if (error) throw error;

    // Abre o navegador com a URL de autenticação
    await Browser.open({ url: data.url });

    // Configura o listener para o Deep Link
    App.addListener('appUrlOpen', async ({ url }) => {
      if (url.includes('auth/callback')) {
        // Fecha o navegador
        await Browser.close();

        // Extrai os tokens da URL
        const params = new URLSearchParams(url.split('#')[1]);
        const access_token = params.get('access_token');
        const refresh_token = params.get('refresh_token');

        if (!access_token) throw new Error('Tokens não encontrados na URL');

        // Configura a sessão no Supabase
        const { data: { user }, error: sessionError } = await supabase.auth.setSession({
          access_token,
          refresh_token: refresh_token || ''
        });

        if (sessionError) throw sessionError;

        // Se o usuário não existir na tabela users, cria o perfil
        if (user) {
          const { data: existingUser } = await supabase
            .from('users')
            .select()
            .eq('id', user.id)
            .single();

          if (!existingUser) {
            await supabase
              .from('users')
              .insert([{
                id: user.id,
                email: user.email,
                nome: user.user_metadata.full_name || user.user_metadata.name || user.email?.split('@')[0],
                foto_perfil: user.user_metadata.avatar_url,
                tipo_de_conta: 'free'
              }]);
          }
        }

        // Dispara evento de login bem-sucedido
        window.dispatchEvent(new Event('login-successful'));
      }
    });

  } catch (error) {
    console.error('Erro no login com Google:', error);
    throw handleError(error);
  }
}

// Função única e limpa para login com Apple
export async function signInWithApple(): Promise<void> {
  try {
    // Remove listeners antigos para evitar conflitos
    App.removeAllListeners();

    // Inicia o processo de OAuth
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: 'com.biblia.em.louvor.app://auth/callback',
        skipBrowserRedirect: true
      }
    });

    if (error) {
      throw error;
    }

    // Verifica se a URL contém erro
    if (data.url.includes('503') || data.url.includes('error')) {
      throw new Error('Servidor temporariamente indisponível. Tente novamente em alguns minutos.');
    }

    // Configura o listener para o Deep Link ANTES de abrir o browser
    App.addListener('appUrlOpen', async ({ url }) => {
      if (url.includes('auth/callback')) {
        try {
          // Fecha o navegador
          await Browser.close();

          // Aguarda um pouco para processar
          await new Promise(resolve => setTimeout(resolve, 500));

          // Verifica se a URL contém tokens ou código de erro
          const urlParts = url.split('?')[1] || url.split('#')[1];
          if (!urlParts) {
            throw new Error('URL de callback inválida');
          }

          const params = new URLSearchParams(urlParts);
          const access_token = params.get('access_token');
          const refresh_token = params.get('refresh_token');
          const code = params.get('code');
          const error_code = params.get('error');

          if (error_code) {
            throw new Error(`Erro na autenticação: ${error_code}`);
          }

          if (!access_token && !code) {
            throw new Error('Tokens não encontrados na URL');
          }

          if (access_token) {
            // Configura a sessão no Supabase
            const { data: { user }, error: sessionError } = await supabase.auth.setSession({
              access_token,
              refresh_token: refresh_token || ''
            });

            if (sessionError) {
              throw sessionError;
            }

            // Se o usuário não existir na tabela users, cria o perfil
            if (user) {
              const { data: existingUser } = await supabase
                .from('users')
                .select()
                .eq('id', user.id)
                .single();

              if (!existingUser) {
                await supabase
                  .from('users')
                  .insert([{
                    id: user.id,
                    email: user.email,
                    nome: user.user_metadata?.full_name || 
                          user.user_metadata?.name || 
                          user.email?.split('@')[0] || 
                          'Usuário Apple',
                    foto_perfil: user.user_metadata?.avatar_url,
                    tipo_de_conta: 'free'
                  }]);
              }
            }

            // Dispara evento de login bem-sucedido
            window.dispatchEvent(new Event('login-successful'));
          }
        } catch (callbackError) {
          // Dispara evento de erro
          window.dispatchEvent(new CustomEvent('login-error', { 
            detail: { message: callbackError instanceof Error ? callbackError.message : 'Erro na autenticação' }
          }));
        }
      }
    });

    // Abre o navegador com a URL de autenticação
    await Browser.open({ url: data.url });

  } catch (error) {
    throw handleError(error);
  }
} 